import { ANY_EMPLOYEE_ID } from '@/features/booking-selection/constants';
import { EXTRA_TIME_REQUIREMENT, ExtraTimeRequirement } from '@/types/state/book';
import { Place } from '@/types/state/shared';
import { resolveEmployeeFromUrl } from './resolveEmployeeFromUrl';
import { resolveMainServicesFromUrl } from './resolveMainServicesFromUrl';
import { BookingFlowStepSelection } from './steps';
import { parseSelection } from './urlState';

/**
 * Derives the steps.ts selection synchronously from the URL — the pure, read-only twin of
 * `useBookingFlowSelection`'s dispatching hydration, for guards and continue handlers.
 * Guards must read the URL, never `state.book`: on a cold load the store is one render
 * behind the URL and a store-read guard would bounce a valid deep link.
 */
export function deriveStepSelection(
  place: Place,
  search: string,
  calendarOffersEmployeePick: boolean,
  user?: unknown,
): BookingFlowStepSelection {
  const urlSelection = parseSelection(search);

  // ss resolves through the shared helper so an add-on-flagged id reads as "nothing picked"
  // here exactly as it does in hydration; rs ids are looked up as-is — they name add-ons.
  const mainServices = resolveMainServicesFromUrl(urlSelection.ss, place);
  const rsIds = urlSelection.rs ? urlSelection.rs.split(',').map((id) => parseInt(id, 10)) : [];
  const pickedAddOns = (place.services ?? [])
    .flatMap((category) => category.services ?? [])
    .filter(
      (service) =>
        service.id != null && rsIds.includes(service.id) && !mainServices.some((main) => main.id === service.id),
    );
  const services = [...mainServices, ...pickedAddOns];

  const { matched: matchedEmployee, isAnyEmployee } = resolveEmployeeFromUrl(urlSelection.sr, place.employees);
  const employee = matchedEmployee ? matchedEmployee.id : isAnyEmployee ? ANY_EMPLOYEE_ID : undefined;

  const extraTimeRequirement: ExtraTimeRequirement | undefined =
    urlSelection.et === '1'
      ? EXTRA_TIME_REQUIREMENT.NeedsExtraTime
      : urlSelection.et === '0'
      ? EXTRA_TIME_REQUIREMENT.NoExtraTime
      : undefined;

  return { place, services, employee, extraTimeRequirement, user, calendarOffersEmployeePick };
}
